import { useQuery } from '@tanstack/react-query'
import { useGetList, useGetOne } from 'ra-core'
import { httpClient } from '@internal/ui-kit/lib/httpClient'
import type { z } from 'zod'
import type {
  brandOptionSchema,
  insuranceSchema,
  inventorySchema,
  pointsAccountSchema,
  pointsTransactionSchema,
  productOptionSchema,
  referralDiscountCodeSchema,
  slimProductSchema,
  userEventSchema,
  userOrderSchema,
} from './schema'

type UserOrder = z.infer<typeof userOrderSchema>
type UserEvent = z.infer<typeof userEventSchema>
type PointsAccount = z.infer<typeof pointsAccountSchema>
type PointsTransaction = z.infer<typeof pointsTransactionSchema>
type Inventory = z.infer<typeof inventorySchema>
type Insurance = z.infer<typeof insuranceSchema>
type ReferralDiscountCode = z.infer<typeof referralDiscountCodeSchema>
type ProductOption = z.infer<typeof productOptionSchema>

/**
 * `getUserOrders` (`orders/all?limit=50&owner_id={user_id}`) — a bespoke,
 * owner-scoped, capped-at-50 fetch, not the same shape as `apps/orders`' own
 * paginated `orders` `dataProvider` resource (`orders/all/paid`, cursor
 * pagination); bypasses `dataProvider` the same way `apps/orders`'
 * `useOrderEvents` does for its own single-purpose scoped GET.
 */
export function useUserOrders(userId: string | undefined) {
  const { data, isPending, error } = useQuery({
    queryKey: ['user-orders', userId],
    queryFn: () => httpClient<UserOrder[]>(`/orders/all?limit=50&owner_id=${userId}`),
    enabled: Boolean(userId),
  })
  return { orders: data ?? [], isPending, error }
}

/** `getUserEvents` (`events/by_patient/{user_id}`) — same bespoke-fetch reasoning as `useUserOrders` above. */
export function useUserEvents(userId: string | undefined) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['user-events', userId],
    queryFn: () => httpClient<UserEvent[]>(`/events/by_patient/${userId}`),
    enabled: Boolean(userId),
  })
  return { events: data ?? [], isPending, error, refetch }
}

/** `getPointsAccount` (`v2/public/users/{user_id}/points/account`) — a single nested-path GET, same reasoning `apps/doctor-profiles`' `usePaymentInfo` documents. */
export function usePointsAccount(userId: string | undefined) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['user-points-account', userId],
    queryFn: () => httpClient<PointsAccount>(`/v2/public/users/${userId}/points/account`),
    enabled: Boolean(userId),
  })
  return { pointsAccount: data, isPending, error, refetch }
}

/** `getPointsTransactions` (`v2/public/users/{user_id}/points/transactions`). */
export function usePointsTransactions(userId: string | undefined) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['user-points-transactions', userId],
    queryFn: () => httpClient<PointsTransaction[]>(`/v2/public/users/${userId}/points/transactions`),
    enabled: Boolean(userId),
  })
  return { transactions: data ?? [], isPending, error, refetch }
}

/** `getInventories` (`users/{user_id}/inventories`). */
export function useInventories(userId: string | undefined) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['user-inventories', userId],
    queryFn: () => httpClient<Inventory[]>(`/users/${userId}/inventories`),
    enabled: Boolean(userId),
  })
  return { inventories: data ?? [], isPending, error, refetch }
}

/** `getInsurances` (`insurances/{user_id}`). */
export function useInsurances(userId: string | undefined) {
  const { data, isPending, error } = useQuery({
    queryKey: ['user-insurances', userId],
    queryFn: () => httpClient<Insurance[]>(`/insurances/${userId}`),
    enabled: Boolean(userId),
  })
  return { insurances: data ?? [], isPending, error }
}

/** `getReferralDiscountCode` (`v2/public/users/{user_id}/referrals/discount-code`). */
export function useReferralDiscountCode(userId: string | undefined) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['user-referral-discount-code', userId],
    queryFn: () => httpClient<ReferralDiscountCode>(`/v2/public/users/${userId}/referrals/discount-code`),
    enabled: Boolean(userId),
  })
  return { referralDiscountCode: data, isPending, error, refetch }
}

/**
 * `getSlimProducts` (`products/slim`) — the booking drawer's product picker.
 * Goes through the real `slim-products` `dataProvider` resource
 * (`apps/doctor-profiles`' `main_product_id` reference already registers
 * it), not a bespoke fetch, since it's a plain fetch-everything list.
 */
export function useSlimProducts() {
  const { data, isPending, error } = useGetList<z.infer<typeof slimProductSchema> & { id: string }>('slim-products', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'title', order: 'ASC' },
  })
  return { products: data ?? [], isPending, error }
}

/**
 * `fetchSelectedProduct` (`products/{id}`) — the booking drawer's option
 * picker, cascading off the chosen product. Goes through the real
 * `products` `dataProvider` resource's `getOne` (`apps/products` already
 * owns it), not a bespoke fetch.
 */
export function useProductOptions(productId: string | undefined) {
  const { data, isPending, error } = useGetOne<{ id: string; options?: ProductOption[] }>(
    'products',
    { id: productId as string },
    { enabled: Boolean(productId) },
  )
  return { options: data?.options ?? [], isPending, error }
}

/**
 * `getBrands` (`brands`) — the inventory tab's "assign inventory" dialog
 * brand multi-select. Goes through the real `brands` `dataProvider` resource
 * (`apps/brands` already owns it), not a bespoke fetch, same "plain
 * fetch-everything list" reasoning as `useSlimProducts` above.
 */
export function useBrandOptions() {
  const { data, isPending, error } = useGetList<z.infer<typeof brandOptionSchema> & { id: string }>('brands', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  })
  return { brands: data ?? [], isPending, error }
}
